import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getShop } from '../api/shop.api';

// Port từ app mobile (store/ServiceTabsStore.js) — giữ logic y hệt, chỉ đổi
// AsyncStorage -> localStorage và lấy shopType qua getShop().

const TAB_LABELS = {
    sanpham: 'Sản phẩm',
    dichvu: 'Dịch vụ',
    combo: 'Combo',
    lieutrinh: 'Gói dịch vụ',
};

const ALL_TAB_KEYS = ['sanpham', 'dichvu', 'combo', 'lieutrinh'];

// Cấu hình mặc định chung (khi chưa biết ngành) — hiện tất cả theo thứ tự gốc
const DEFAULT_TABS = [
    { key: 'sanpham', label: 'Sản phẩm', visible: true, order: 0 },
    { key: 'dichvu', label: 'Dịch vụ', visible: true, order: 1 },
    { key: 'combo', label: 'Combo', visible: true, order: 2 },
    { key: 'lieutrinh', label: 'Gói dịch vụ', visible: true, order: 3 },
];

// Thứ tự danh mục mặc định theo từng ngành.
// Các key được liệt kê = hiển thị (đúng thứ tự); key KHÔNG liệt kê = ẩn (visible:false), xếp cuối.
// Lưu ý: shop ngành 'khác' được BE lưu shopType = 'kinhmat', nên dùng chung thứ tự với kính.
const SHOP_TYPE_TAB_ORDER = {
    spa: ['dichvu', 'sanpham', 'lieutrinh'],
    nails: ['dichvu', 'sanpham', 'combo'],
    salon: ['dichvu', 'sanpham', 'combo'],
    kinhmat: ['sanpham', 'dichvu', 'combo'],
    vatlieu: ['sanpham', 'combo', 'dichvu'],
    khac: ['sanpham', 'dichvu', 'combo'],
};

// Trả về cấu hình tabs mặc định theo ngành (nếu có), không thì cấu hình chung.
export function buildDefaultTabs(shopType) {
    const orderKeys = SHOP_TYPE_TAB_ORDER[shopType];
    return orderKeys ? buildTabsForOrder(orderKeys) : DEFAULT_TABS;
}

// Dựng mảng tabs từ danh sách key hiển thị theo thứ tự; key còn lại đưa xuống cuối và ẩn.
function buildTabsForOrder(orderKeys) {
    const tabs = orderKeys.map((key, i) => ({
        key,
        label: TAB_LABELS[key],
        visible: true,
        order: i,
    }));
    let nextOrder = orderKeys.length;
    ALL_TAB_KEYS.forEach((key) => {
        if (!orderKeys.includes(key)) {
            tabs.push({ key, label: TAB_LABELS[key], visible: false, order: nextOrder++ });
        }
    });
    return tabs;
}

// Kiểm tra tabs hiện tại có đúng cấu hình mặc định chung không (tức khách CHƯA tự chỉnh).
// Dùng để chỉ áp cấu hình theo ngành cho shop chưa đụng vào, còn khách đã sửa thì giữ nguyên.
function isUntouchedDefault(tabs) {
    if (!Array.isArray(tabs) || tabs.length !== DEFAULT_TABS.length) return false;
    const sorted = [...tabs].sort((a, b) => a.order - b.order);
    return sorted.every(
        (t, i) => t.key === DEFAULT_TABS[i].key && t.visible === DEFAULT_TABS[i].visible,
    );
}

const useServiceTabsStore = create(
    persist(
        (set) => ({
            tabs: DEFAULT_TABS,

            // Đã áp cấu hình mặc định theo ngành (1 lần duy nhất) hay chưa
            typeDefaultApplied: false,

            setTabs: (tabs) => set({ tabs }),

            toggleVisible: (key) =>
                set((state) => ({
                    tabs: state.tabs.map((t) => (t.key === key ? { ...t, visible: !t.visible } : t)),
                })),

            moveUp: (key) =>
                set((state) => {
                    const sorted = [...state.tabs].sort((a, b) => a.order - b.order);
                    const idx = sorted.findIndex((t) => t.key === key);
                    if (idx <= 0) return state;
                    const newTabs = [...sorted];
                    const prevOrder = newTabs[idx - 1].order;
                    const curOrder = newTabs[idx].order;
                    newTabs[idx - 1] = { ...newTabs[idx - 1], order: curOrder };
                    newTabs[idx] = { ...newTabs[idx], order: prevOrder };
                    return { tabs: newTabs };
                }),

            moveDown: (key) =>
                set((state) => {
                    const sorted = [...state.tabs].sort((a, b) => a.order - b.order);
                    const idx = sorted.findIndex((t) => t.key === key);
                    if (idx >= sorted.length - 1) return state;
                    const newTabs = [...sorted];
                    const nextOrder = newTabs[idx + 1].order;
                    const curOrder = newTabs[idx].order;
                    newTabs[idx + 1] = { ...newTabs[idx + 1], order: curOrder };
                    newTabs[idx] = { ...newTabs[idx], order: nextOrder };
                    return { tabs: newTabs };
                }),

            reset: () => set({ tabs: DEFAULT_TABS }),

            // Áp cấu hình danh mục mặc định theo ngành — CHỈ chạy thật 1 lần.
            // - Đã áp trước đó (typeDefaultApplied) -> bỏ qua.
            // - Khách đã tự chỉnh tabs -> tôn trọng, chỉ đánh dấu đã áp.
            // - Ngành lạ / null -> giữ cấu hình hiện tại, đánh dấu đã áp.
            applyTypeDefault: (shopType) =>
                set((state) => {
                    if (state.typeDefaultApplied) return state;

                    const orderKeys = SHOP_TYPE_TAB_ORDER[shopType];
                    if (!orderKeys) return { typeDefaultApplied: true };

                    if (!isUntouchedDefault(state.tabs)) return { typeDefaultApplied: true };

                    return { tabs: buildTabsForOrder(orderKeys), typeDefaultApplied: true };
                }),
        }),
        {
            name: 'service-tabs-store',
            version: 4,
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                tabs: state.tabs,
                typeDefaultApplied: state.typeDefaultApplied,
            }),
            migrate: (persistedState, version) => {
                if (!persistedState?.tabs) return persistedState;

                // v1 → v2: thêm tab 'lieutrinh' nếu chưa có
                if (version < 2) {
                    const hasLieutrinh = persistedState.tabs.some((t) => t.key === 'lieutrinh');
                    if (!hasLieutrinh) {
                        const maxOrder = persistedState.tabs.reduce(
                            (m, t) => Math.max(m, t.order ?? 0),
                            -1,
                        );
                        persistedState.tabs = [
                            ...persistedState.tabs,
                            { key: 'lieutrinh', label: 'Gói dịch vụ', visible: true, order: maxOrder + 1 },
                        ];
                    }
                }

                // v2 → v3: force rename label cũ "Liệu trình" thành "Gói dịch vụ"
                if (version < 3) {
                    persistedState.tabs = persistedState.tabs.map((t) =>
                        t.key === 'lieutrinh' ? { ...t, label: 'Gói dịch vụ' } : t,
                    );
                }

                // v3 → v4: chưa từng có cờ typeDefaultApplied -> để false để áp cấu hình
                // theo ngành 1 lần ở lần vào app kế tiếp (chỉ áp nếu khách chưa tự chỉnh).
                if (version < 4 && persistedState.typeDefaultApplied === undefined) {
                    persistedState.typeDefaultApplied = false;
                }

                return persistedState;
            },
        },
    ),
);

// Trả về danh sách key tab type (không gồm 'all') đang hiển thị, đúng thứ tự cấu hình.
// supportedKeys (tuỳ chọn): chỉ giữ các tab mà màn hình hiện hỗ trợ (vd web chưa có 'lieutrinh').
export function selectVisibleTabKeys(tabs, supportedKeys) {
    return [...tabs]
        .filter((t) => t.visible && (!supportedKeys || supportedKeys.includes(t.key)))
        .sort((a, b) => a.order - b.order)
        .map((t) => t.key);
}

// Đảm bảo áp cấu hình danh mục mặc định theo ngành ĐÚNG 1 LẦN cho shop hiện tại.
// Lấy shopId từ localStorage('user'), shopType từ BE rồi áp qua applyTypeDefault.
// An toàn để gọi nhiều nơi (vào trang admin, sau đăng nhập, sau tạo shop): cờ guard chống lặp.
// Có thể truyền sẵn shopType (vd AdminLayout đã fetch shop) để khỏi gọi lại API.
export async function ensureShopTypeTabDefault(preloadedShopType) {
    try {
        if (useServiceTabsStore.getState().typeDefaultApplied) return;

        let shopType = preloadedShopType;
        if (shopType === undefined) {
            const stored = localStorage.getItem('user');
            if (!stored) return;
            const user = JSON.parse(stored);
            const shopId = user?.shopId;
            if (!shopId) return;
            const res = await getShop(shopId);
            shopType = res?.data?.shopType ?? null;
        }
        useServiceTabsStore.getState().applyTypeDefault(shopType ?? null);
    } catch (e) {
        // Lỗi mạng/parse -> bỏ qua, cờ vẫn false nên sẽ thử lại lần sau
    }
}

export default useServiceTabsStore;
